/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import downloadjs from 'downloadjs';
import { QSIconButton } from '../../../../../shared/components/atoms';
import CloseIcon from '../../../../../shared/icons/Close';
import { MessageTypesEnum } from '../../typings/message-types';
import ReactDOM from 'react-dom';
import { useAtom, useAtomValue } from 'jotai';
import { mediaViewerAtomV2 } from '../../states/media-viewer-state';
import DownloadIcon from '../../../../../shared/icons/Download';
import { Button, ClickAwayListener } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { QSTypography } from '../../../../../shared/components/atoms';
import PDFIcon from '../../../../../shared/icons/PDF';
import { useGetToken } from '../../../../../shared/hooks/useGetToken';
import { getUrlWithToken } from '../../../../../shared/utils/getMediaURLWithToken';

export const MediaViewerV2 = () => {
  const [state, dispatch] = useAtom(mediaViewerAtomV2);

  const types = state.messageTypes;
  const [zoom, setZoom] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const portalContainer = document.getElementById('portal')!;
  const userToken = useGetToken();

  const handlePrevious = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    if (state.activeIndex > 0) {
      dispatch({
        ...state,
        activeIndex: state.activeIndex - 1,
      });
    }
  };

  const handleNext = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    if (state.activeIndex < state.filesUrl.length - 1) {
      dispatch({
        ...state,
        activeIndex: state.activeIndex + 1,
      });
    }
  };

  const handleMouseScroll = useCallback(
    (event: MouseEvent) => {
      const image = event.target as HTMLImageElement;
      if (image.tagName !== 'IMG') {
        return;
      }
      const rect = image.getBoundingClientRect();

      const offsetX = ((event.clientX - rect.left) / rect.width) * 99;
      const offsetY = ((event.clientY - rect.top) / rect.height) * 99;

      setZoomOrigin({ x: offsetX, y: offsetY });
    },
    [zoom]
  );
  const handleZoom = (event: React.MouseEvent<HTMLImageElement>) => {
    const image = event.currentTarget;

    // Calculate the cursor position relative to the image
    const rect = image.getBoundingClientRect();
    const offsetX = ((event.clientX - rect.left) / rect.width) * 100;
    const offsetY = ((event.clientY - rect.top) / rect.height) * 100;

    setZoomOrigin({ x: offsetX, y: offsetY });
    setZoom((prevZoom) => !prevZoom);
  };

  const fileNameAndExtension = useMemo(() => {
    if (!state.filesUrl[state.activeIndex]) return { name: '', extension: '' };
    const fileName = state.filesUrl[state.activeIndex].substring(
      state.filesUrl[state.activeIndex].lastIndexOf('/') + 1
    );
    if (fileName.includes('.')) {
      const name = fileName.split('.').slice(0, -1).join('.');
      const extension = fileName.split('.').pop();
      return { name, extension };
    } else {
      return { name: fileName, extension: '' };
    }
  }, [state.filesUrl]);

  const getFileExtensionFromMimeType = (mimeType: string): string => {
    const mimeTypesMap: Record<string, string> = {
      // Document MIME types
      'text/plain': 'txt',
      'application/pdf': 'pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/msword': 'doc',
      'application/vnd.ms-powerpoint': 'ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',

      // Image MIME types
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/bmp': 'bmp',
      'image/tiff': 'tiff',
      'image/svg+xml': 'svg',

      // Video MIME types
      'video/mp4': 'mp4',
      'video/mpeg': 'mpeg',
      'video/ogg': 'ogv',
      'video/webm': 'webm',
      'video/quicktime': 'mov',
      'video/x-msvideo': 'avi',
      'video/x-ms-wmv': 'wmv',
      'video/3gp': '3gp',

      // Audio MIME types
      'audio/aac': 'aac',
      'audio/mpeg': 'mp3',
      'audio/mp4': 'm4a',
      'audio/amr': 'amr',
      'audio/ogg': 'ogg',
      'audio/ogg; codecs=opus': 'opus',
    };

    return mimeTypesMap[mimeType] || '';
  };

  const handleMediaDownload = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      let urlWithQueryParams = getUrlWithToken(state.filesUrl[state.activeIndex], userToken ?? '');
      fetch(urlWithQueryParams, {
        method: 'GET',
        headers: {},
      })
        .then((response) => {
          response.arrayBuffer().then(function (buffer) {
            const url = window.URL.createObjectURL(new Blob([buffer]));
            const contentType = response.headers.get('content-type');
            const extension =
              fileNameAndExtension.extension === '' || fileNameAndExtension.extension === 'null'
                ? getFileExtensionFromMimeType(contentType ?? '')
                : fileNameAndExtension.extension;
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${fileNameAndExtension.name}.${extension}`);
            document.body.appendChild(link);
            link.click();
          });
        })
        .catch(() => {
          downloadjs(
            state.filesUrl[state.activeIndex],
            `${fileNameAndExtension.name}.${fileNameAndExtension.extension}`,
            'image/png'
          );
        });
    },
    [fileNameAndExtension.extension, fileNameAndExtension.name, state.filesUrl]
  );

  useEffect(() => {
    document.getElementById('media-viewer-container')?.addEventListener('mousemove', handleMouseScroll);

    return () => {
      document.getElementById('media-viewer-container')?.removeEventListener('mousemove', handleMouseScroll);
    };
  }, []);

  const handleClose = () => {
    dispatch({
      isMediaViewerV2Active: false,
      messageTypes: [MessageTypesEnum.IMAGE],
      filesUrl: [],
      activeIndex: 0,
    });
  };

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [dispatch, handleClose]);

  const url = state.filesUrl[state.activeIndex];

  return ReactDOM.createPortal(
    <div
      id="media-viewer-container"
      css={css`
        height: 100vh;
        width: 100vw;
        z-index: 99999;
        padding: 24px;
        position: absolute;
        background: rgba(17, 27, 33, 0.95);
        top: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      `}
    >
      {!zoom && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 10px;
            `}
          >
            <QSIconButton
              sx={{
                background: 'none',
              }}
              onClick={handleMediaDownload}
            >
              <DownloadIcon color="white" />
            </QSIconButton>
            <QSIconButton
              sx={{
                background: 'none',
              }}
              onClick={handleClose}
            >
              <CloseIcon color="white" />
            </QSIconButton>
          </div>
        </div>
      )}

      <div
        css={css`
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        `}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div
            css={css`
              display: flex;
              transition: transform 0.3s ease-in-out;
              transform: translateX(-${state.activeIndex * 100}%);
              width: ${state.filesUrl.length * 100}%;
              height: 100%;
            `}
          >
            {state.filesUrl.map((url, index) => (
              <div
                key={url}
                css={css`
                  min-width: 100%;
                  height: 100%;
                  max-height: 90%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0 20px;
                `}
              >
                {types[index] === MessageTypesEnum.IMAGE && (
                  <img
                    src={url}
                    alt=""
                    css={css`
                      max-width: 100%;
                      max-height: 90%;
                      object-fit: contain;
                    `}
                  />
                )}
                {types[index] === MessageTypesEnum.VIDEO && (
                  <video
                    src={url}
                    controls
                    css={css`
                      max-width: 100%;
                      max-height: 90%;
                      object-fit: contain;
                    `}
                  ></video>
                )}
                {types[index] === MessageTypesEnum.AUDIO && <audio src={url} controls />}
                {types[index] === MessageTypesEnum.DOCUMENT && <PDFIcon style={{ width: '60%', height: '60%' }} />}
              </div>
            ))}
          </div>
        </ClickAwayListener>
        {/* Left Arrow */}
        {state.activeIndex > 0 && (
          <Button
            onClick={handlePrevious}
            css={css`
              position: absolute;
              left: 20px;
              top: 50%;
              transform: translateY(-50%);
              background: rgba(0, 0, 0, 0.5);
              border: none;
              height: 48px;
              width: 48px;
              min-width: 48px;
              color: #fff;
              border-radius: 50%;
              z-index: 2;
              cursor: pointer;
              &:hover {
                background: rgba(0, 0, 0, 0.7);
              }
            `}
          >
            <ArrowBackIcon />
          </Button>
        )}
        {/* Right Arrow */}
        {state.activeIndex < state.filesUrl.length - 1 && (
          <Button
            onClick={handleNext}
            css={css`
              position: absolute;
              right: 20px;
              top: 50%;
              transform: translateY(-50%);
              background: rgba(0, 0, 0, 0.5);
              border: none;
              height: 48px;
              width: 48px;
              min-width: 48px;
              color: #fff;
              border-radius: 50%;
              z-index: 2;
              cursor: pointer;
              &:hover {
                background: rgba(0, 0, 0, 0.7);
              }
            `}
          >
            <ArrowForwardIcon />
          </Button>
        )}
      </div>
    </div>,
    portalContainer
  );
};
